import { useCallback, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import * as S from './styles';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  body: string;
  ctaLabel: string;
  ctaHref?: string;
  ctaOnClick?: () => void;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  body,
  ctaLabel,
  ctaHref,
  ctaOnClick,
}) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);

      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);

      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  const handleContainerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  if (!mounted) return null;

  return ReactDOM.createPortal(
    <S.Overlay isOpen={isOpen} onClick={onClose}>
      <S.Container isOpen={isOpen} onClick={handleContainerClick}>
        <S.CloseButton onClick={onClose} aria-label="Close">
          ×
        </S.CloseButton>

        <S.Title>{title}</S.Title>

        <S.Body>{body}</S.Body>

        <S.CTALink
          href={ctaHref}
          onClick={ctaOnClick}
          as={ctaHref ? 'a' : 'button'}
          target={ctaHref ? '_blank' : undefined}
          rel={ctaHref ? 'noopener noreferrer' : undefined}
        >
          {ctaLabel}
        </S.CTALink>
      </S.Container>
    </S.Overlay>,
    document.body,
  );
};
